import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Lock, Loader2, CheckCircle, XCircle } from 'lucide-react';
import paymentService from '../services/paymentService';
import Swal from 'sweetalert2';

const MastercardPaymentModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  onError,
  amount, 
  currency = 'LKR',
  orderId,
  customerInfo = {},
  bookingData = null
}) => {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, creating-session, ready, processing, success, error
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);
  const [checkoutConfigured, setCheckoutConfigured] = useState(false);
  const scriptLoadedRef = useRef(false);
  const currentSessionIdRef = useRef(null); // Track the current valid session ID

  useEffect(() => {
    if (isOpen && amount > 0) {
      // Reset states when modal opens - ensure clean state for new booking
      setSessionId(null);
      setCheckoutLoaded(false);
      setCheckoutConfigured(false);
      setPaymentStatus('idle');
      scriptLoadedRef.current = false;
      currentSessionIdRef.current = null; // Clear the ref too
      
      // Step 1: Load checkout script and establish session
      initializePayment();
    } else if (!isOpen) {
      // Clear session ID when modal closes to prevent reuse
      setSessionId(null);
      setCheckoutConfigured(false);
      setPaymentStatus('idle');
      currentSessionIdRef.current = null; // Clear the ref too
    }
  }, [isOpen, amount, orderId]); // Add orderId to dependencies to ensure new session for new booking

  // Step 1: Load Mastercard Checkout Script
  const loadCheckoutScript = () => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.Checkout && scriptLoadedRef.current) {
        setCheckoutLoaded(true);
        resolve(window.Checkout);
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="checkout.min.js"]');
      if (existingScript) {
        // Script tag exists, wait for it to load
        if (window.Checkout) {
          scriptLoadedRef.current = true;
          setCheckoutLoaded(true);
          resolve(window.Checkout);
          return;
        }
        // Wait a bit for script to load
        const checkInterval = setInterval(() => {
          if (window.Checkout) {
            clearInterval(checkInterval);
            scriptLoadedRef.current = true;
            setCheckoutLoaded(true);
            resolve(window.Checkout);
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.Checkout) {
            reject(new Error('Checkout script timeout'));
          }
        }, 10000);
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://bankofceylon.gateway.mastercard.com/static/checkout/checkout.min.js';
      
      // Define callbacks before loading script
      window.errorCallback = (error) => {
        console.error('Mastercard Checkout Error:', error);
        setPaymentStatus('error');
        setLoading(false);
        
        Swal.fire({
          icon: 'error',
          title: 'Payment Error',
          text: error.explanation || error.message || 'An error occurred during payment processing.',
          confirmButtonColor: '#dc2626'
        });

        if (onError) {
          onError(error);
        }
      };

      window.cancelCallback = () => {
        setPaymentStatus('ready');
        setLoading(false);
        
        Swal.fire({
          icon: 'info',
          title: 'Payment Cancelled',
          text: 'You cancelled the payment process.',
          confirmButtonColor: '#6b7280'
        });
      };

      window.successCallback = (result) => {
        setPaymentStatus('success');
        setLoading(false);
        
        // Call onSuccess callback with payment result
        // This will trigger handlePaymentSuccess in BusBooking.js
        if (onSuccess) {
          onSuccess({
            success: true,
            orderId: orderId,
            transactionId: result.transactionId || result.orderId || result.resultIndicator,
            resultIndicator: result.resultIndicator,
            amount: amount,
            currency: currency
          });
        }
        
        // Close modal after a short delay to show success message
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 2000);
      };

      // Set callback attributes
      script.setAttribute('data-error', 'errorCallback');
      script.setAttribute('data-cancel', 'cancelCallback');
      script.setAttribute('data-complete', 'successCallback');
      
      script.async = true;
      script.onload = () => {
        // Wait a bit for Checkout object to be available
        const checkInterval = setInterval(() => {
          if (window.Checkout) {
            clearInterval(checkInterval);
            scriptLoadedRef.current = true;
            setCheckoutLoaded(true);
            resolve(window.Checkout);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.Checkout) {
            reject(new Error('Checkout object not found after script load'));
          }
        }, 10000);
      };
      script.onerror = () => {
        reject(new Error('Failed to load Mastercard Checkout script'));
      };
      
      document.head.appendChild(script);
    });
  };

  const initializePayment = async () => {
    setLoading(true);
    setPaymentStatus('creating-session');

    try {
      // Load checkout script first
      await loadCheckoutScript();

      // Build return URL for payment callback
      const returnUrl = `${window.location.origin}/payment/callback`;

      // Establish payment session with backend
      const sessionResponse = await paymentService.establishSession(
        orderId,
        amount,
        currency,
        customerInfo,
        returnUrl
      );

      if (!sessionResponse.sessionId) {
        throw new Error('Failed to get payment session ID');
      }

      // Store the new session ID - this is the fresh session ID for this booking
      const newSessionId = sessionResponse.sessionId;
      
      // Update the ref FIRST to mark this as the current valid session
      currentSessionIdRef.current = newSessionId;
      setSessionId(newSessionId);
      console.log('✅ New Session ID received:', newSessionId);

      // Step 2: Configure Checkout with NEW session ID only
      // Note: For API version 67+, displayControl must be in INITIATE_CHECKOUT (backend),
      // not in configure(). Only session object is allowed in configure().
      // Always configure with the newly received session ID to avoid using old sessions
      if (window.Checkout) {
        try {
          // Configure with the new session ID - don't use state here to avoid stale values
          window.Checkout.configure({
            session: {
              id: newSessionId  // Use the newSessionId variable directly, not from state
            }
          });
          setCheckoutConfigured(true);
          console.log('✅ Checkout configured with session ID:', newSessionId);
        } catch (configError) {
          console.error('Error configuring checkout:', configError);
          throw new Error('Failed to configure checkout: ' + configError.message);
        }
      } else {
        throw new Error('Checkout object not available');
      }

      // Step 3: Store booking data before redirecting
      if (bookingData) {
        sessionStorage.setItem('pendingBookingData', JSON.stringify(bookingData));
        sessionStorage.setItem('pendingPayment', JSON.stringify({
          orderId,
          amount,
          currency,
          sessionId: newSessionId,
          timestamp: Date.now()
        }));
      }

      // Step 4: Set status to ready and show loading
      setPaymentStatus('ready');
      setLoading(true);
      
      // Step 5: Redirect to hosted payment page
      // This prevents session conflicts that occur with embedded checkout
      console.log('🔄 Redirecting to hosted payment page with session ID:', newSessionId);
      
      // Wait a moment to show the loading state, then redirect
      setTimeout(() => {
        try {
          // Use showPaymentPage() to redirect to hosted checkout (like sample code)
          window.Checkout.showPaymentPage();
        } catch (redirectError) {
          console.error('Error redirecting to payment page:', redirectError);
          setLoading(false);
          throw new Error('Failed to redirect to payment page: ' + redirectError.message);
        }
      }, 1500); // Show loading for 1.5 seconds before redirect
      
    } catch (error) {
      console.error('Error initializing payment:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setPaymentStatus('error');
      setLoading(false);
      
      // Show detailed error message
      const errorMessage = error.message || 'Failed to initialize payment. Please try again.';
      
      Swal.fire({
        icon: 'error',
        title: 'Payment Initialization Failed',
        html: `<div style="text-align: left;">
          <p><strong>Error:</strong> ${errorMessage}</p>
          <p style="font-size: 12px; color: #666; margin-top: 10px;">
            Please check the browser console (F12) for more details.
          </p>
        </div>`,
        confirmButtonColor: '#dc2626',
        width: '500px'
      });

      if (onError) {
        onError(error);
      }
    }
  };

  // Removed showEmbeddedCheckout - now using redirect-based payment (showPaymentPage)

  const formatAmount = (amt) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amt);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Complete Payment</h2>
              <p className="text-sm text-gray-500">Secure payment via Mastercard</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading || paymentStatus === 'processing'}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Payment Amount */}
        <div className="p-4 bg-gray-50 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Total Amount</span>
            <span className="text-2xl font-bold text-gray-900">
              {formatAmount(amount)}
            </span>
          </div>
          {/* Order ID and Session ID hidden from user view - only logged in console for debugging */}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          {/* Payment Status - Error */}
          {paymentStatus === 'error' && (
            <div className="p-4 bg-red-50 border-b">
              <div className="flex items-center space-x-3">
                <XCircle className="h-6 w-6 text-red-600" />
                <div>
                  <p className="font-semibold text-red-900">Payment Failed</p>
                  <p className="text-sm text-red-700">Please try again or use a different payment method.</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State - Creating Session and Redirecting */}
          {loading && (
            <div className="p-6 text-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-600">
                {paymentStatus === 'creating-session' 
                  ? 'Preparing secure payment page...' 
                  : 'Redirecting to payment page...'}
              </p>
              <p className="text-sm text-gray-500 mt-2">You will be redirected to complete your payment</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex-shrink-0">
          {/* Payment Method Images */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 text-center mb-2">Accepted Payment Methods</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <img 
                src="/images/visa.png" 
                alt="Visa" 
                className="h-10 w-14 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <img 
                src="/images/master.png" 
                alt="Mastercard" 
                className="h-10 w-14 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <img 
                src="/images/Verifid by visa.jpg" 
                alt="Verified by Visa" 
                className="h-10 w-14 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <img 
                src="/images/master card secure gate.jpg" 
                alt="Mastercard Secure" 
                className="h-10 w-14 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
            <Lock className="h-3 w-3" />
            <span>Secured by Mastercard Payment Gateway</span>
          </div>
          <p className="text-xs text-gray-400 text-center mt-1">
            Your card details are encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default MastercardPaymentModal;
